// src/components/AdminAvisoEditor.tsx
import React, { useEffect, useState } from "react";
import axiosInstance from "utils/axiosInstance";

// Si tenés un Modal global reutilizable, podés importarlo.
// Si no, el componente usa un modal simple inline.
import Modal from "components/Modal"; // si no existe, ver nota abajo
import { Button } from "components/ui";

const AdminAvisoEditor: React.FC = () => {
  const [texto, setTexto] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasActiveAviso, setHasActiveAviso] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);

  // Obtener aviso activo al montar (y para renderizar el botón correctamente)
  useEffect(() => {
    let mounted = true;
    const fetchAviso = async () => {
      try {
        const { data } = await axiosInstance.get("/aviso");
        if (!mounted) return;
        setHasActiveAviso(Boolean(data));
      } catch (err) {
        console.warn("No se pudo obtener aviso (admin):", err);
      }
    };
    fetchAviso();
    return () => { mounted = false; };
  }, []);

  // Publicar
  const guardar = async () => {
    if (!texto.trim()) {
      alert("El texto del aviso no puede estar vacío.");
      return;
    }
    try {
      setLoading(true);
      await axiosInstance.post("/aviso", { texto });
      setHasActiveAviso(true);
      setShowPublishModal(false);
      setTexto("");
      // Notificar a otras partes de la app (GlobalModal) que cambió el aviso
      window.dispatchEvent(new Event("aviso-changed"));
      alert("Aviso publicado");
    } catch (err) {
      console.error("Error al publicar aviso:", err);
      alert("Error al publicar aviso");
    } finally {
      setLoading(false);
    }
  };

  // Desactivar
  const desactivar = async () => {
    const ok = window.confirm("¿Desactivar el aviso actual?");
    if (!ok) return;
    try {
      setLoading(true);
      await axiosInstance.delete("/aviso");
      setHasActiveAviso(false);
      // Notificar a otras partes de la app (GlobalModal) que cambió el aviso
      window.dispatchEvent(new Event("aviso-changed"));
      alert("Aviso desactivado");
    } catch (err) {
      console.error("Error al desactivar aviso:", err);
      alert("Error al desactivar aviso");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-1 border-b border-gray-200/80 pb-2 dark:border-white/10">
      {hasActiveAviso ? (
        <Button
          variant="destructive-soft"
          size="sm"
          fullWidth
          onClick={desactivar}
          loading={loading}
        >
          Desactivar aviso
        </Button>
      ) : (
        <>
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            onClick={() => setShowPublishModal(true)}
          >
            Publicar aviso
          </Button>

          {showPublishModal && (
            <Modal
              isOpen={showPublishModal}
              onClose={() => setShowPublishModal(false)}
              size="lg"
            >
              <h3 className="text-[17px] font-semibold text-gray-900 dark:text-white">
                Publicar aviso
              </h3>
              <p className="mb-4 mt-1 text-sm text-gray-700 dark:text-gray-400">
                Se muestra a todos al abrir la app. Acepta Markdown.
              </p>
              <textarea
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder="Escribí el aviso..."
                className="h-44 w-full resize-y rounded-field border border-gray-200 bg-white p-3 text-sm leading-relaxed text-gray-900 transition-colors placeholder:text-gray-600 hover:border-gray-300 dark:border-white/10 dark:bg-navy-900 dark:text-white dark:hover:border-white/20"
              />
              <div className="mt-6 flex justify-end gap-2 border-t border-gray-200/80 pt-5 dark:border-white/10">
                <Button
                  variant="secondary"
                  onClick={() => setShowPublishModal(false)}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button variant="primary" onClick={guardar} loading={loading}>
                  Publicar
                </Button>
              </div>
            </Modal>
          )}
        </>
      )}
    </div>
  );
};

export default AdminAvisoEditor;
